import { useState } from 'react';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Flag, Loader2, AlertTriangle } from 'lucide-react';
import { extractErrorMessage } from '@/lib/api-errors';

const REPORT_REASONS = [
  { value: 'inappropriate_content', label: 'Conteúdo Impróprio' },
  { value: 'spam', label: 'Spam' },
  { value: 'harassment', label: 'Assédio' },
  { value: 'hate_speech', label: 'Discurso de Ódio' },
  { value: 'violence', label: 'Violência' },
  { value: 'plagiarism', label: 'Plágio' },
  { value: 'underage_content', label: 'Conteúdo com Menores' },
  { value: 'other', label: 'Outro' },
];

const CONTENT_TYPE_LABELS = {
  story: 'conto',
  comment: 'comentário',
  confession: 'confissão',
  article: 'artigo',
};

export default function ReportModal({ 
  isOpen, 
  onClose, 
  contentType, 
  contentId,
  contentTitle 
}) {
  const [reason, setReason] = useState('');
  const [description, setDescription] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async () => {
    if (!reason) {
      toast.error('Por favor, seleciona um motivo');
      return;
    }

    setSubmitting(true);
    try {
      await api.post('/reports', {
        content_type: contentType,
        content_id: contentId,
        reason: reason,
        description: description || null
      });

      toast.success('Relatório enviado com sucesso', {
        description: 'A nossa equipa irá analisar o conteúdo reportado.'
      });
      
      // Reset form and close
      setReason('');
      setDescription('');
      onClose();
    } catch (error) {
      const message = extractErrorMessage(error, 'Erro ao enviar relatório');
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    setReason('');
    setDescription('');
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="bg-surface border-border sm:max-w-md" data-testid="report-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-foreground">
            <Flag className="w-5 h-5 text-red-500" />
            Reportar {CONTENT_TYPE_LABELS[contentType] || 'conteúdo'}
          </DialogTitle>
          <DialogDescription>
            {contentTitle && (
              <span className="block mt-1 text-sm text-muted-foreground truncate">
                "{contentTitle}"
              </span>
            )}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Warning */}
          <div className="flex items-start gap-3 p-3 rounded-lg bg-amber-500/10 border border-amber-500/20">
            <AlertTriangle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-amber-200/80">
              Relatórios falsos ou abusivos podem resultar em suspensão da tua conta.
            </p>
          </div>

          {/* Reason Select */}
          <div className="space-y-2">
            <Label htmlFor="reason" className="text-foreground">Motivo *</Label>
            <Select value={reason} onValueChange={setReason}>
              <SelectTrigger 
                id="reason"
                className="bg-surface-light border-border text-foreground"
                data-testid="report-reason-select"
              >
                <SelectValue placeholder="Seleciona um motivo" />
              </SelectTrigger>
              <SelectContent className="bg-surface border-border">
                {REPORT_REASONS.map((r) => (
                  <SelectItem 
                    key={r.value} 
                    value={r.value}
                    className="text-foreground hover:bg-surface-light"
                  >
                    {r.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Description */}
          <div className="space-y-2">
            <Label htmlFor="description" className="text-foreground">
              Descrição adicional (opcional)
            </Label>
            <Textarea
              id="description"
              placeholder="Descreva o problema com mais detalhe..."
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="bg-surface-light border-border text-foreground placeholder:text-muted-foreground resize-none"
              rows={3}
              maxLength={500}
              data-testid="report-description"
            />
            <p className="text-xs text-muted-foreground text-right">
              {description.length}/500
            </p>
          </div>
        </div>

        <DialogFooter className="gap-2 sm:gap-0">
          <Button
            variant="outline"
            onClick={handleClose}
            className="border-border"
          >
            Cancelar
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={submitting || !reason}
            className="bg-red-600 hover:bg-red-700 text-white"
            data-testid="report-submit-btn"
          >
            {submitting ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                A enviar...
              </>
            ) : (
              <>
                <Flag className="w-4 h-4 mr-2" />
                Enviar Relatório
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
